import cx from "clsx";
import { GalleryAlbum } from "kreate-common/modules/business-types";
import ButtonTransparentV2 from "kreate-common/modules/kreate-ui/components/ButtonTransparentV2";
import DropdownMenu from "kreate-common/modules/kreate-ui/components/DropdownMenu";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import Link from "next/link";
import React from "react";
import { MdDelete, MdDragIndicator, MdEdit, MdMoreVert } from "react-icons/md";
import {
  RiBringForward,
  RiBringToFront,
  RiSendBackward,
  RiSendToBack,
} from "react-icons/ri";

import { useGallerySidebarApi } from "../../hooks/useGallerySidebarApi";
import GalleryAlbumAvatar from "../GalleryAlbumAvatar";

import styles from "./index.module.scss";

import {
  formatInteger$Compact,
  formatInteger$Standard,
} from "@/modules/common-utils/format";
import { getLinkToPageGalleryCollector$Album } from "@/modules/navigation/utils/getLinkTo";
import { useIsDraggingOver } from "@/modules/priorities/hooks/useIsDraggingOver";
import { cmp } from "@/modules/priorities/utils";

type Props = {
  className?: string;
  style?: React.CSSProperties;
  album: GalleryAlbum;
  api: ReturnType<typeof useGallerySidebarApi>;
  collapsed: boolean;
};

export default function GalleryAlbumItem({
  className,
  style,
  album,
  api,
  collapsed,
}: Props) {
  const [container, setContainer] = React.useState<HTMLElement | null>(null);
  const isDraggingOver = useIsDraggingOver(
    api.draggedAlbum.value ? container : null
  );

  const isSelected =
    api.params.sidebarLocation.type === "albums" &&
    api.params.sidebarLocation.galleryAlbumId === album.galleryAlbumId;

  const isDragged =
    api.draggedAlbum.value?.galleryAlbumId === album.galleryAlbumId;

  const reorderingInfo = api.getReorderingInfo(album);
  const linkToGalleryAlbum = getLinkToPageGalleryCollector$Album(
    api.params.addressIsh,
    album
  );

  return (
    <div
      ref={setContainer}
      className={cx(
        styles.container,
        className,
        isSelected ? styles.selected : undefined,
        collapsed ? styles.collapsed : undefined,
        isDragged ? styles.beingDragged : undefined
      )}
      style={style}
      // For the drop event to fire at all, you have to cancel the defaults
      // of both the dragover and the dragenter event.
      onDragEnter={withDefaultPrevented(() => undefined)}
      onDragLeave={withDefaultPrevented(() => undefined)}
      onDragOver={withDefaultPrevented(() => undefined)}
      onDrop={withDefaultPrevented(() => {
        if (
          api.draggedAlbum.value &&
          reorderingInfo?.priorityIfDropHere != null
        ) {
          void api.setAlbumPriority?.(
            api.draggedAlbum.value,
            reorderingInfo.priorityIfDropHere
          );
        }
      })}
    >
      <Link className={styles.content} href={linkToGalleryAlbum}>
        <Flex.Row gap="16px">
          <Flex.Cell flex="0 0 auto">
            <GalleryAlbumAvatar
              imageFiles={album.avatarFiles || []}
              size="64px"
              selected={isSelected}
            />
          </Flex.Cell>
          <Flex.Col
            className={styles.itemDetails}
            flex="1 1 auto"
            minWidth="0"
            justifyContent="center"
          >
            <div className={styles.itemTitle} title={album.title}>
              {album.title}
            </div>
            <div
              className={styles.itemStats}
              title={formatInteger$Standard(album.totalNumArtworks)}
            >
              {[formatInteger$Compact(album.totalNumArtworks), "items"].join(
                " "
              )}
            </div>
          </Flex.Col>
        </Flex.Row>
      </Link>
      {!collapsed && api.isLoggedInUserGalleryOwner ? (
        <DropdownMenu.Menu
          trigger={
            <ButtonTransparentV2
              className={styles.buttonIcon}
              icon={<MdMoreVert size="24px" />}
            />
          }
        >
          <DropdownMenu.Item
            icon={<MdEdit />}
            content="Edit Exhibit Name"
            onSelect={() => api.updateAlbumTitle?.(album)}
          />
          <DropdownMenu.Item
            icon={<MdEdit />}
            content="Edit Exhibit Handle"
            onSelect={() => api.updateAlbumHandle?.(album)}
          />
          <DropdownMenu.Item
            icon={<MdDelete />}
            content="Remove Exhibit"
            onSelect={() => api.deleteAlbum?.(album)}
          />
          <DropdownMenu.Item
            icon={<RiBringToFront />}
            content="Bring to Front"
            disabled={reorderingInfo?.priorityIfBringToFront == null}
            onSelect={() =>
              reorderingInfo?.priorityIfBringToFront != null
                ? api.setAlbumPriority?.(
                    album,
                    reorderingInfo.priorityIfBringToFront
                  )
                : undefined
            }
          />
          <DropdownMenu.Item
            icon={<RiBringForward />}
            content="Bring Forward"
            disabled={reorderingInfo?.priorityIfBringForward == null}
            onSelect={() =>
              reorderingInfo?.priorityIfBringForward != null
                ? api.setAlbumPriority?.(
                    album,
                    reorderingInfo.priorityIfBringForward
                  )
                : undefined
            }
          />
          <DropdownMenu.Item
            icon={<RiSendBackward />}
            content="Send Backward"
            disabled={reorderingInfo?.priorityIfSendBackward == null}
            onSelect={() =>
              reorderingInfo?.priorityIfSendBackward != null
                ? api.setAlbumPriority?.(
                    album,
                    reorderingInfo.priorityIfSendBackward
                  )
                : undefined
            }
          />
          <DropdownMenu.Item
            icon={<RiSendToBack />}
            content="Send to Back"
            disabled={reorderingInfo?.priorityIfSendToBack == null}
            onSelect={() =>
              reorderingInfo?.priorityIfSendToBack != null
                ? api.setAlbumPriority?.(
                    album,
                    reorderingInfo.priorityIfSendToBack
                  )
                : undefined
            }
          />
        </DropdownMenu.Menu>
      ) : undefined}
      {!collapsed &&
      api.isLoggedInUserGalleryOwner &&
      reorderingInfo?.priorityIfDropHere != null &&
      isDraggingOver ? (
        <div
          className={cx(
            styles.droppingIndicator,
            cmp(reorderingInfo.priorityIfDropHere, album.priority) === +1
              ? styles.movingUp
              : undefined,
            cmp(reorderingInfo.priorityIfDropHere, album.priority) === -1
              ? styles.movingDown
              : undefined
          )}
        />
      ) : undefined}
      {!collapsed && api.isLoggedInUserGalleryOwner ? (
        <div
          className={cx(
            styles.dragHandle,
            api.busy ? styles.disabled : undefined
          )}
          draggable={!api.busy}
          onDragStart={(event) => {
            if (container) {
              event.dataTransfer.setDragImage(
                container,
                event.currentTarget.offsetLeft +
                  event.currentTarget.offsetWidth / 2,
                event.currentTarget.offsetTop
              );
            }
            api.draggedAlbum.setValue(album);
          }}
          onDragEnd={() => {
            api.draggedAlbum.setValue(undefined);
          }}
        >
          <MdDragIndicator size="24px" />
        </div>
      ) : undefined}
    </div>
  );
}

function withDefaultPrevented<T extends Event | React.SyntheticEvent>(
  handler: (event: T) => void
) {
  return (event: T) => {
    event.preventDefault();
    event.stopPropagation();
    handler(event);
    return false;
  };
}
